import Collapse from "@material-ui/core/Collapse";
import { makeStyles } from "@material-ui/core/styles";
import { useEffect, useRef, useState } from "react";
import { changeAppState } from "../../../../../../../../../../common/appState";
import ShurikenProgress from "../../../../../../../../../shared/Animations/ShurikenProgress";
import { PrimaryButton } from "../../../../../../../../../shared/Button/PrimaryButton";
import {
    RelatedUser,
    User,
    UserForFriendList,
} from "../../../../../../../../../shared/User/types";
import { fetchUsersForRanking } from "../fetchUsersForRanking";
import {
    FriendRecord,
    myPageFriendCardScrollableContainerHeight,
} from "./FriendRecord";

let newFriendIds: number[] = [];

const friendsSaveKey = "FriendsSaveKey";
export function FriendsTab({
    friends,
    user,
}: {
    friends: RelatedUser[];
    user: User;
}) {
    const [usersForFriendList, setUsersForFriendList] = useState<{
        isLoading: boolean;
        data: UserForFriendList[];
    }>({ isLoading: true, data: [] });

    useEffect(() => {
        return () => {
            newFriendIds = [];
        };
    }, []);

    useEffect(() => {
        const savedData = localStorage.getItem(friendsSaveKey);
        if (savedData) {
            setUsersForFriendList({
                isLoading: true,
                data: JSON.parse(savedData),
            });
        }

        fetchUsersForRanking(friends.map(f => f.userId)).then(u => {
            const _newFriendIds = friends
                .filter(f => f.isNewFriend)
                .map(f => f.userId);
            if (_newFriendIds.length > 0) {
                // Even when "isNewFriend" disappears because of the fetch, the status will be kept in front-end.
                newFriendIds = _newFriendIds;
            }
            const data = u
                .map(f => ({
                    ...f,
                    isNewFriend: newFriendIds.includes(f.userId),
                }))
                .sort((a, b) => {
                    if (a.isNewFriend === b.isNewFriend) {
                        return 0;
                    }
                    return a.isNewFriend ? -1 : 1;
                });
            setUsersForFriendList({
                isLoading: false,
                data,
            });
            (async () => {
                localStorage.setItem(friendsSaveKey, JSON.stringify(data));
            })();
        });
    }, [friends.map(f => f.userId).join("_")]);

    return <FriendList friends={usersForFriendList} user={user} />;
}

function FriendList({
    friends,
    user,
}: {
    friends: {
        isLoading: boolean;
        data: UserForFriendList[];
    };
    user: User;
}) {
    const c = useStyles();
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    useEffect(() => {
        const el = scrollableContainerRef.current;
        if (!el) {
            return;
        }
        const onScroll = () => {
            setScrollTop(el.scrollTop);
        };
        el.addEventListener("scroll", onScroll);
        return () => {
            el.removeEventListener("scroll", onScroll);
        };
    }, []);

    return (
        <div className={c.container} ref={scrollableContainerRef}>
            <Collapse in={friends.isLoading}>
                <ShurikenProgress size={20} style={{ height: 50 }} />
            </Collapse>

            {friends.data.map((friend, i) => (
                <FriendRecord
                    key={friend.userId}
                    friend={friend}
                    colorNumber={i + 1}
                    user={user}
                    scrollableContainerEl={scrollableContainerRef.current}
                    scrollTop={scrollTop}
                    index={i}
                />
            ))}

            {!friends.isLoading && friends.data.length === 0 && (
                <div className={c.noData}>
                    <PrimaryButton
                        onClick={() => {
                            changeAppState("activeUsersPanelState", {
                                open: true,
                            });
                        }}
                    >
                        Find New Friends!
                    </PrimaryButton>
                </div>
            )}
        </div>
    );
}
const useStyles = makeStyles(theme => ({
    container: {
        backgroundColor: theme.palette.grey[100],
        height: myPageFriendCardScrollableContainerHeight,
        maxHeight: myPageFriendCardScrollableContainerHeight,
        flex: 1,
        fontSize: "large",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
    },
    noData: {
        width: "100%",
        height: 305,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.grey[600],
    },
}));
